import React, { useState, useEffect, useCallback, useRef } from "react";
import apiClient from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Download, RefreshCw, BookOpen, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CashbookStatsCards } from "@/components/cashbook/CashbookStatsCards";
import { CashbookTable } from "@/components/cashbook/CashbookTable";
import { EditPaymentDialog, DeletePaymentDialog } from "@/components/cashbook/CashbookDialogs";

const FilterTabs = ({ activeFilter, onFilterChange, counts }) => {
  const tabs = [
    { key: "all", label: "Tous", count: counts.all },
    { key: "subscription", label: "Inscriptions", count: counts.subscription },
    { key: "manual", label: "Entrées manuelles", count: counts.manual },
  ];
  return (
    <div className="flex gap-2 mb-4" data-testid="filter-tabs">
      {tabs.map(tab => (
        <button
          key={tab.key}
          onClick={() => onFilterChange(tab.key)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeFilter === tab.key ? "bg-[#3899ec] text-white" : "bg-[#f4f7fa] text-[#577083] hover:bg-[#edf0f4]"
          }`}
          data-testid={`filter-${tab.key}`}
        >
          {tab.label}
          {tab.count !== undefined && (
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${activeFilter === tab.key ? "bg-white/20" : "bg-[#c1c9d0]/30"}`}>
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

const CashbookPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState({ by: "date", order: "desc" });
  const [entryTypeFilter, setEntryTypeFilter] = useState("all");
  const [filterCounts, setFilterCounts] = useState({ all: 0, subscription: 0, manual: 0 });
  const [stats, setStats] = useState(null);
  const [periodFilter, setPeriodFilter] = useState(null);
  const [periodLabel, setPeriodLabel] = useState(null);
  const observerRef = useRef(null);
  const lastPaymentRef = useRef(null);
  const LIMIT = 50;

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingPayment, setDeletingPayment] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => { if (!isAdmin) navigate("/dashboard"); }, [isAdmin, navigate]);

  const fetchStats = useCallback(async () => {
    try { const res = await apiClient.get("/payments/cashbook/stats"); setStats(res.data); }
    catch (err) { console.error("Error fetching stats:", err); }
  }, []);

  const fetchPayments = useCallback(async (currentOffset = 0, append = false, sortBy = sort.by, sortOrder = sort.order, filter = entryTypeFilter, period = periodFilter) => {
    currentOffset === 0 ? setLoading(true) : setLoadingMore(true);
    setError(null);
    try {
      let url = `/payments/cashbook?offset=${currentOffset}&limit=${LIMIT}&sort_by=${sortBy}&sort_order=${sortOrder}&entry_type=${filter}`;
      if (period) url += `&period=${period}`;
      const res = await apiClient.get(url);
      const data = res.data;
      setPayments(prev => append ? [...prev, ...data.payments] : data.payments);
      setTotal(data.total);
      setHasMore(data.has_more);
      setOffset(currentOffset + data.payments.length);
      if (data.period_label) setPeriodLabel(data.period_label);
      if (currentOffset === 0 && !period) fetchFilterCounts();
    } catch (err) {
      console.error("Error fetching cashbook:", err);
      setError("Erreur lors du chargement du livre de caisse");
    } finally { setLoading(false); setLoadingMore(false); }
  }, [sort.by, sort.order, entryTypeFilter, periodFilter]);

  const fetchFilterCounts = async () => {
    try {
      const [allRes, subRes, manualRes] = await Promise.all([
        apiClient.get("/payments/cashbook?offset=0&limit=1&entry_type=all"),
        apiClient.get("/payments/cashbook?offset=0&limit=1&entry_type=subscription"),
        apiClient.get("/payments/cashbook?offset=0&limit=1&entry_type=manual"),
      ]);
      setFilterCounts({ all: allRes.data.total, subscription: subRes.data.total, manual: manualRes.data.total });
    } catch (err) { console.error("Error fetching filter counts:", err); }
  };

  const resetAndFetch = (sortBy, sortOrder, filter, period) => {
    setPayments([]); setOffset(0); setHasMore(true);
    fetchPayments(0, false, sortBy, sortOrder, filter, period);
  };

  const handleSort = (sortKey) => {
    const newOrder = sort.by === sortKey && sort.order === "desc" ? "asc" : "desc";
    setSort({ by: sortKey, order: newOrder });
    resetAndFetch(sortKey, newOrder, entryTypeFilter, periodFilter);
  };

  const handleFilterChange = (filter) => {
    setEntryTypeFilter(filter);
    resetAndFetch(sort.by, sort.order, filter, periodFilter);
  };

  const handlePeriodClick = (period) => {
    if (periodFilter === period) {
      setPeriodFilter(null); setPeriodLabel(null);
      resetAndFetch(sort.by, sort.order, entryTypeFilter, null);
    } else {
      setPeriodFilter(period);
      resetAndFetch(sort.by, sort.order, entryTypeFilter, period);
    }
  };

  const clearPeriodFilter = () => {
    setPeriodFilter(null); setPeriodLabel(null);
    resetAndFetch(sort.by, sort.order, entryTypeFilter, null);
  };

  const handleRefresh = () => {
    resetAndFetch(sort.by, sort.order, entryTypeFilter, periodFilter);
    fetchStats();
  };

  useEffect(() => {
    fetchPayments();
    fetchStats();
  }, [fetchPayments, fetchStats]);

  // Infinite scroll
  useEffect(() => {
    if (loading || loadingMore || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting && hasMore && !loadingMore) fetchPayments(offset, true, sort.by, sort.order, entryTypeFilter, periodFilter); },
      { threshold: 0.1 }
    );
    if (lastPaymentRef.current) observer.observe(lastPaymentRef.current);
    observerRef.current = observer;
    return () => { if (observerRef.current) observerRef.current.disconnect(); };
  }, [loading, loadingMore, hasMore, offset, sort.by, sort.order, entryTypeFilter, periodFilter, fetchPayments]);

  // Edit
  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setEditForm({ amount: payment.price_eur, nature: payment.nature || "", payer_last_name: payment.last_name, payer_first_name: payment.first_name, payment_type: payment.payment_type });
    setEditModalOpen(true);
  };
  const handleEditSave = async () => {
    if (!editingPayment) return;
    setEditLoading(true);
    try { await apiClient.put(`/payments/manual/${editingPayment.id}`, editForm); setEditModalOpen(false); setEditingPayment(null); handleRefresh(); }
    catch (err) { console.error("Error updating payment:", err); alert("Erreur lors de la mise à jour"); }
    finally { setEditLoading(false); }
  };

  // Delete
  const handleDeleteClick = (payment) => { setDeletingPayment(payment); setDeleteConfirmOpen(true); };
  const handleDeleteConfirm = async () => {
    if (!deletingPayment) return;
    setDeleteLoading(true);
    try { await apiClient.delete(`/payments/manual/${deletingPayment.id}`); setDeleteConfirmOpen(false); setDeletingPayment(null); handleRefresh(); }
    catch (err) { console.error("Error deleting payment:", err); alert("Erreur lors de la suppression"); }
    finally { setDeleteLoading(false); }
  };

  // Export
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await apiClient.get("/payments/cashbook/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a"); link.href = url;
      const cd = res.headers["content-disposition"];
      link.setAttribute("download", cd?.match(/filename=([^;]+)/)?.[1]?.trim() || `livre_caisse_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
    } catch (err) { console.error("Error exporting CSV:", err); alert("Erreur lors de l'export CSV"); }
    finally { setExporting(false); }
  };

  if (!isAdmin) return null;

  return (
    <div className="crm-page" data-testid="cashbook-page">
      <div className="crm-page-header">
        <div className="flex items-center justify-between w-full">
          <div>
            <h1 className="crm-page-title flex items-center gap-2">
              <BookOpen size={24} className="text-[#22c55e]" /> Livre de Caisse
            </h1>
            <p className="crm-page-subtitle">Paiements à l'inscription — Hors domiciliations — 2 dernières années</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading} className="text-[#3899ec] border-[#3899ec]/30 hover:bg-[#3899ec]/10" data-testid="refresh-cashbook-btn">
              <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Actualiser
            </Button>
            <Button variant="default" size="sm" onClick={handleExportCSV} disabled={exporting || payments.length === 0} className="bg-[#22c55e] hover:bg-[#16a34a] text-white" data-testid="export-csv-btn">
              <Download size={14} className={`mr-1 ${exporting ? "animate-pulse" : ""}`} /> {exporting ? "Export..." : "Exporter CSV"}
            </Button>
          </div>
        </div>
      </div>

      <CashbookStatsCards stats={stats} periodFilter={periodFilter} onPeriodClick={handlePeriodClick} />

      {periodFilter && (
        <div className="bg-[#f4f7fa] rounded-lg p-3 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-[#577083]" />
            <span className="text-sm text-[#162d3d]">Filtre actif : <strong>{periodLabel}</strong> ({total} paiement{total > 1 ? "s" : ""})</span>
          </div>
          <Button variant="ghost" size="sm" onClick={clearPeriodFilter} className="text-[#577083] hover:text-red-500">Retirer le filtre</Button>
        </div>
      )}

      <FilterTabs activeFilter={entryTypeFilter} onFilterChange={handleFilterChange} counts={filterCounts} />

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-red-700" data-testid="error-message">{error}</div>}

      <CashbookTable
        payments={payments} loading={loading} loadingMore={loadingMore} hasMore={hasMore} total={total}
        sort={sort} onSort={handleSort}
        onEdit={handleEdit} onDelete={handleDeleteClick} onViewMember={(id) => navigate(`/members/${id}`)}
        lastPaymentRef={lastPaymentRef}
      />

      <EditPaymentDialog open={editModalOpen} onOpenChange={setEditModalOpen} editForm={editForm} setEditForm={setEditForm} onSave={handleEditSave} loading={editLoading} />
      <DeletePaymentDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen} payment={deletingPayment} onConfirm={handleDeleteConfirm} loading={deleteLoading} />
    </div>
  );
};

export default CashbookPage;
